import { useState, KeyboardEvent, useRef, useEffect } from 'react'
import { useController, UseFormReturn } from 'react-hook-form'
import {
    autoUpdate,
    flip,
    FloatingNode,
    offset,
    useClick,
    useDismiss,
    useFloating,
    useFloatingNodeId,
    useInteractions,
    useTransitionStatus,
} from '@floating-ui/react'
import clsx from 'clsx'

import { CodeType, FilterType } from '@/types/logs'
import { InputFormValues } from '@/modules/CombinedLogs/types'
import { useGetCodes } from '@/modules/CombinedLogs/api/useGetCodes'
import { useCombinedLogsTableFormStateStore, useCombinedLogsTableStateStore } from '@/modules/CombinedLogs/store'
import { Input } from '@/ui/Input'
import styles from './TableFormCodeInput.module.scss'
import { Code } from '../../information/Code'
import { useCachedGetCodeName } from '@/modules/CombinedLogs/hooks/useCachedGetCodeName'

interface Props {
    name: keyof InputFormValues
    form: UseFormReturn<InputFormValues>
    isLinked?: boolean
}

type Option = {
    id: string
    label: string
    value: string
}

export const TableFormCodeInput = ({ form, name, isLinked = false }: Props) => {
    const { field, formState } = useController({ control: form.control, name })
    const [isOpen, setIsOpen] = useState(false)
    const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null)
    const nodeId = useFloatingNodeId()
    const { data: codes = [] } = useGetCodes()
    const { setCode } = useCombinedLogsTableFormStateStore()
    const { filter } = useCombinedLogsTableStateStore()
    const rootRef = useRef<HTMLDivElement>(null)
    const getCodeName = useCachedGetCodeName()

    const eventLogId = form.watch('eventLogId')

    const isDailyCodesDisplayed = (filter.type === FilterType.DailyLog || filter.type === FilterType.All) && !eventLogId
    const isMileageLossCodesDisplayed = filter.type === FilterType.MileageLoss || filter.type === FilterType.All

    const mileageCodes = codes.reduce<Option[]>((acc, item) => {
        if (item.type !== CodeType.MileageLoss) {
            return acc
        }

        acc.push({
            id: item.code,
            label: item.code,
            value: item.code,
        })

        return acc
    }, [])

    const infoCodes = codes.reduce<Option[]>((acc, item) => {
        if (item.type !== CodeType.DailyLog) {
            return acc
        }

        acc.push({
            id: item.code,
            label: item.description,
            value: item.code,
        })

        return acc
    }, [])

    const allCodes =
        filter.type === FilterType.MileageLoss || isLinked
            ? mileageCodes
            : filter.type === FilterType.DailyLog
              ? infoCodes
              : [...mileageCodes, ...infoCodes]

    const { x, y, strategy, context, refs } = useFloating({
        nodeId,
        open: isOpen,
        onOpenChange: setIsOpen,
        whileElementsMounted: (reference, floating, update) => {
            const cleanup = autoUpdate(reference, floating, update)
            const handleScroll = (event: Event) => {
                if (floating && event.target === floating) return
                setIsOpen(false)
            }

            const scrollContainer = rootRef.current?.closest('[data-scrollable-table]')
            if (scrollContainer) {
                scrollContainer.addEventListener('scroll', handleScroll)
            }

            return () => {
                cleanup()
                if (scrollContainer) {
                    scrollContainer.removeEventListener('scroll', handleScroll)
                }
            }
        },
        middleware: [offset({ mainAxis: 5 }), flip()],
        placement: 'bottom-start',
    })

    useEffect(() => {
        if (!formState?.defaultValues?.code) {
            field.onChange('')
        }
    }, [formState?.defaultValues?.code])

    const dismiss = useDismiss(context)
    const click = useClick(context)

    const { isMounted, status } = useTransitionStatus(context)
    const { getFloatingProps } = useInteractions([click, dismiss])

    const filteredOptions = allCodes.filter((option) =>
        option.label.toLowerCase().includes(String(field.value ?? '').toLowerCase())
    )

    const handleSelect = (selectedValue: Option) => {
        if (!field.onChange) {
            return
        }

        field.onChange(selectedValue.value)
        setCode(selectedValue.value)
        setIsOpen(false)

        setTimeout(() => {
            const routeInput = document.querySelector('input[placeholder="Route"]')
            if (routeInput instanceof HTMLElement) {
                routeInput.focus()
                return
            }
        }, 50)
    }

    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Escape') {
            setIsOpen(false)
        }
        if (e.key === 'ArrowDown') {
            e.preventDefault()
            setHighlightedIndex((prev) => (prev === null || prev === filteredOptions.length - 1 ? 0 : prev + 1))
        }
        if (e.key === 'ArrowUp') {
            e.preventDefault()
            setHighlightedIndex((prev) => (prev === null || prev === 0 ? filteredOptions.length - 1 : prev - 1))
        }
        if (e.key === 'ArrowRight') {
            if (!field.value) {
                setHighlightedIndex((prev) =>
                    prev === null ? 0 : prev + mileageCodes.length > allCodes.length ? prev : prev + mileageCodes.length
                )
            }
        }
        if (e.key === 'ArrowLeft') {
            if (!field.value) {
                setHighlightedIndex((prev) =>
                    prev === null ? 0 : prev - mileageCodes.length < 0 ? prev : prev - mileageCodes.length
                )
            }
        }
        if (e.key === 'Enter') {
            e.preventDefault()

            if (highlightedIndex !== null && filteredOptions[highlightedIndex]) {
                handleSelect(filteredOptions[highlightedIndex])
                return
            }

            if (filteredOptions.length >= 1) {
                handleSelect(filteredOptions[0])
                return
            }
        }

        if (e.key === 'Tab') {
            if (highlightedIndex !== null && filteredOptions[highlightedIndex]) {
                handleSelect(filteredOptions[highlightedIndex])
                return
            }

            if (filteredOptions.length >= 1) {
                setTimeout(() => {
                    const routeInput = document.querySelector('input[placeholder="Route"]')
                    if (routeInput instanceof HTMLElement) {
                        routeInput.focus()
                        return
                    }
                }, 50)
                return
            }
        }

        // Allow user to delete current code
        if (e.key !== 'Backspace' && e.key !== 'Delete') {
            // User may not try to type a code, user must select
            // from the dropdown
            e.preventDefault()
            return
        }

        if (e.key === 'Backspace' || e.key === 'Delete') {
            e.preventDefault()
            field.onChange('')
            setCode('')
            return
        }
    }

    return (
        <>
            {!field.value || isOpen ? (
                <div onKeyDown={handleKeyDown} ref={rootRef}>
                    <div ref={refs.setReference}>
                        <Input
                            value={field.value}
                            onChange={(e) => field.onChange(e.target.value)}
                            onFocus={() => setIsOpen(true)}
                            onBlur={() => setIsOpen(false)}
                            ref={field.ref}
                            fullWidth
                            variant="table"
                            transparent
                            placeholder="Code"
                            name={name}
                            className={styles.codeInput}
                        />
                    </div>

                    <FloatingNode id={nodeId}>
                        {isMounted && filteredOptions.length > 0 && (
                            <div
                                ref={refs.setFloating}
                                style={{ position: strategy, top: y ?? 0, left: x ?? 0, zIndex: 10000 }}
                                className={styles.codeSuggestionMenu}
                                data-status={status}
                                onKeyDown={handleKeyDown}
                                {...getFloatingProps()}
                            >
                                {!field.value ? (
                                    <div
                                        className={clsx(styles.allCodesMenu, {
                                            [styles.dropdownOptionsOpen]: isOpen,
                                        })}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            e.preventDefault()
                                        }}
                                        onMouseDown={(e) => {
                                            e.stopPropagation()
                                            e.preventDefault()
                                        }}
                                    >
                                        {isMileageLossCodesDisplayed && (
                                            <ul className={styles.allCodesMenuColumn} role="listbox">
                                                <p className={styles.allCodesMenuTitle}>Mileage</p>
                                                {mileageCodes.map((option, index) => (
                                                    <li
                                                        key={option.id}
                                                        className={clsx(styles.dropdownOption, {
                                                            [styles.highlighted]: highlightedIndex === index,
                                                        })}
                                                        onClick={() => {
                                                            handleSelect(option)
                                                        }}
                                                        role="option"
                                                    >
                                                        {option.label}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}

                                        {isDailyCodesDisplayed && (
                                            <ul className={styles.allCodesMenuColumn} role="listbox">
                                                <p className={styles.allCodesMenuTitle}>Info codes</p>
                                                {infoCodes.map((option, index) => (
                                                    <li
                                                        key={option.id}
                                                        className={clsx(styles.dropdownOption, {
                                                            [styles.highlighted]:
                                                                highlightedIndex === index + mileageCodes.length,
                                                        })}
                                                        onClick={() => handleSelect(option)}
                                                        role="option"
                                                    >
                                                        {option.label}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                ) : (
                                    <ul
                                        className={clsx(styles.dropdownOptions, {
                                            [styles.dropdownOptionsOpen]: isOpen,
                                        })}
                                        role="listbox"
                                    >
                                        {filteredOptions.map((option, index) => (
                                            <li
                                                key={option.id}
                                                role="option"
                                                className={clsx(styles.dropdownOption, {
                                                    [styles.highlighted]: highlightedIndex === index,
                                                })}
                                                onClick={() => handleSelect(option)}
                                                onMouseEnter={() => setHighlightedIndex(index)}
                                            >
                                                {option.label}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}
                    </FloatingNode>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation()
                        e.preventDefault()
                        setIsOpen(true)
                    }}
                >
                    <Code codeInput={field.value} getCodeName={getCodeName} />
                </button>
            )}
        </>
    )
}
